/**
 * WayfindingRail — Universal editorial navigation recovery
 * 
 * FOUNDER DIRECTIVE: Every page includes a consistent navigation recovery system.
 * Elegant and editorial, not breadcrumb spam.
 * Monochrome shell with scarlet only for active consequence direction.
 * 
 * Pattern: ← Back | ↑ Chapter | ⌂ Home | Next: Related →
 */
import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, ArrowUp, Home, ArrowRight } from 'lucide-react';

const CHAPTER_MAP = {
  // Public narrative
  '/pricing': { parent: '/', parentLabel: 'Home', next: '/play', nextLabel: 'Try Simulation' },
  '/play': { parent: '/', parentLabel: 'Home', next: '/pricing', nextLabel: 'Pricing' },
  '/historical-timeline': { parent: '/', parentLabel: 'Home', next: '/play', nextLabel: 'Simulation' },
  '/estimator': { parent: '/pricing', parentLabel: 'Pricing', next: '/play', nextLabel: 'Simulation' },
  '/partners': { parent: '/', parentLabel: 'Home', next: '/pricing', nextLabel: 'Pricing' },
  // Intelligence
  '/intelligence': { parent: '/', parentLabel: 'Home', next: '/intelligence/discover', nextLabel: 'Discover Chapter' },
  '/intelligence/discover': { parent: '/intelligence', parentLabel: 'Intelligence', next: '/intelligence/memory', nextLabel: 'Memory Chapter' },
  '/intelligence/memory': { parent: '/intelligence', parentLabel: 'Intelligence', next: '/intelligence/momentum', nextLabel: 'Momentum Chapter' },
  '/intelligence/momentum': { parent: '/intelligence', parentLabel: 'Intelligence', next: '/intelligence/world-models', nextLabel: 'World Models' },
  '/intelligence/world-models': { parent: '/intelligence', parentLabel: 'Intelligence', next: '/intelligence/how-it-thinks', nextLabel: 'How It Thinks' },
  '/intelligence/how-it-thinks': { parent: '/intelligence', parentLabel: 'Intelligence', next: '/play', nextLabel: 'Try Simulation' },
  // Simulations
  '/play/rail-partner': { parent: '/play', parentLabel: 'Simulations', next: '/play/sponsor', nextLabel: 'Sponsor Simulation' },
  '/play/sponsor': { parent: '/play', parentLabel: 'Simulations', next: '/play/investor', nextLabel: 'Investor Simulation' },
  '/play/investor': { parent: '/play', parentLabel: 'Simulations', next: '/play/regulator', nextLabel: 'Regulator Simulation' },
  '/play/regulator': { parent: '/play', parentLabel: 'Simulations', next: '/play/agentic-commerce', nextLabel: 'Agentic Commerce' },
  '/play/agentic-commerce': { parent: '/play', parentLabel: 'Simulations', next: '/play/cbdc-benefits', nextLabel: 'CBDC & Benefits' },
  '/play/cbdc-benefits': { parent: '/play', parentLabel: 'Simulations', next: '/pricing', nextLabel: 'Pricing' },
  // Operational
  '/ops/replay-console': { parent: '/dashboard', parentLabel: 'Dashboard', next: '/ops/observability', nextLabel: 'Observability' },
  '/ops/observability': { parent: '/dashboard', parentLabel: 'Dashboard', next: '/ops/replay-console', nextLabel: 'Replay Console' },
  '/crm': { parent: '/dashboard', parentLabel: 'Dashboard', next: '/momentum', nextLabel: 'Momentum' },
  '/momentum': { parent: '/dashboard', parentLabel: 'Dashboard', next: '/crm', nextLabel: 'CRM' },
  '/world-ready': { parent: '/dashboard', parentLabel: 'Dashboard', next: '/ops/observability', nextLabel: 'Observability' },
  // Create & Setup
  '/create': { parent: '/', parentLabel: 'Home', next: '/play', nextLabel: 'Simulation' },
  '/simulation-room': { parent: '/play', parentLabel: 'Simulations', next: '/pricing', nextLabel: 'Pricing' },
};

function getChapterInfo(pathname) {
  // Exact match
  if (CHAPTER_MAP[pathname]) return CHAPTER_MAP[pathname];
  // Prefix match for nested routes
  const prefixes = Object.keys(CHAPTER_MAP).sort((a, b) => b.length - a.length);
  for (const prefix of prefixes) {
    if (pathname.startsWith(prefix + '/')) return CHAPTER_MAP[prefix];
  }
  // Default fallback
  return { parent: '/', parentLabel: 'Home', next: null, nextLabel: null };
}

export function WayfindingRail() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Don't show on homepage or login/register. The SupPlant evaluator surfaces
  // are a product, not a chapter of the marketing narrative: this rail floats
  // over their controls and its Home affordance leaves the sandbox entirely.
  if (pathname === '/' || pathname === '/login' || pathname === '/register' || pathname === '/start') return null;
  if (pathname.startsWith('/sandbox/')) return null;
  if (pathname.startsWith('/granullo/virtual-asset-lab')) return null;
  // The Farmer App and the programme console are products too. The rail
  // floated over the Farmer App's own SIMULATED footer, and its Home
  // affordance sends a beneficiary into the marketing site.
  if (pathname.startsWith('/farmer')) return null;
  if (pathname.startsWith('/my-entitlement')) return null;

  const chapter = getChapterInfo(pathname);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
      data-testid="wayfinding-rail"
      aria-label="Page navigation"
    >
      <div className="max-w-[1280px] mx-auto px-6 pb-5">
        <div
          className="inline-flex items-center gap-1 pointer-events-auto"
          style={{
            background: 'var(--shell-surface, #FFFFFF)',
            border: '1px solid var(--shell-border-exhibit, rgba(0,0,0,0.12))',
            borderRadius: 8,
            padding: '6px 4px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Back */}
          <button
            onClick={() => navigate(-1)}
            className="wayfinding-btn"
            data-testid="wayfinding-back"
            title="Go back"
          >
            <ArrowLeft size={13} />
            <span>Back</span>
          </button>

          <span className="wayfinding-sep" />

          {/* Chapter Parent */}
          {chapter.parent && (
            <>
              <Link
                to={chapter.parent}
                className="wayfinding-btn"
                data-testid="wayfinding-chapter"
                title={chapter.parentLabel}
              >
                <ArrowUp size={13} />
                <span>{chapter.parentLabel}</span>
              </Link>
              <span className="wayfinding-sep" />
            </>
          )}

          {/* Home */}
          <Link
            to="/"
            className="wayfinding-btn"
            data-testid="wayfinding-home"
            title="Home"
          >
            <Home size={13} />
          </Link>

          {/* Next */}
          {chapter.next && (
            <>
              <span className="wayfinding-sep" />
              <Link
                to={chapter.next}
                className="wayfinding-btn wayfinding-next"
                data-testid="wayfinding-next"
                title={chapter.nextLabel}
              >
                <span>{chapter.nextLabel}</span>
                <ArrowRight size={13} />
              </Link>
            </>
          )}
        </div>
      </div>

      <style>{`
        .wayfinding-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.01em;
          color: var(--shell-text-muted, #6B6B6B);
          background: transparent;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          text-decoration: none;
          white-space: nowrap;
          transition: color 200ms ease, background 200ms ease;
        }
        .wayfinding-btn:hover {
          color: var(--shell-text, #0A0A0A);
          background: var(--shell-bg-alt, #F5F5F5);
          text-decoration: none;
        }
        .wayfinding-next {
          color: var(--shell-text-secondary, #3D3D3D);
        }
        .wayfinding-next:hover {
          color: var(--shell-scarlet, #CD2200);
        }
        .wayfinding-sep {
          width: 1px;
          height: 14px;
          background: var(--shell-border, rgba(0,0,0,0.08));
          flex-shrink: 0;
        }
        @media (max-width: 640px) {
          .wayfinding-btn span {
            display: none;
          }
          .wayfinding-btn {
            padding: 6px 8px;
          }
          .wayfinding-next span {
            display: inline;
            max-width: 80px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      `}</style>
    </nav>
  );
}
